import { t } from 'i18next';
import { get, isEqual, isNumber, isPlainObject, isString } from 'lodash';
import type { IConditionValueAssignment, ParameterCombinationDto } from '@/api/generator';
import { toDisplayValue, type NumberDisplayOptionsWithUnit } from '@/utils/numberUtils';

export type Operator = '===' | '!==' | '>' | '<=' | '<' | '>=';

/** `value: null` means "parameter not set" (a valid, complete clause); `value: undefined` means no value chosen yet. */
export type Clause = { param?: string; operator: Operator; value?: number | string | null };

type ParamType = 'number' | 'enum' | 'string';

type EnumSelectOption = {
  title: string;
  value: string | number | null;
};

type NumberParamInfo = {
  min?: number | null;
  max?: number | null;
  format: NumberDisplayOptionsWithUnit;
  availableValues?: number[];
};

export type ParamInfo = {
  label: string;
} & (
  | ({ type: 'number' } & NumberParamInfo)
  | { type: 'string' }
  | { type: 'enum'; options: EnumSelectOption[] }
);

export const operatorLabels: Record<Operator, string> = {
  '===': '=',
  '!==': '≠',
  '>': '>',
  '<=': '≤',
  '<': '<',
  '>=': '≥',
};

const ASSIGNMENTS: keyof ParameterCombinationDto = 'conditionValueAssignments';
const SETUP_PARAMETER_ID: keyof IConditionValueAssignment = 'setupParameterId';
const VALUE: keyof IConditionValueAssignment = 'value';

const allOperators = Object.keys(operatorLabels) as Operator[];
const equalityOperators: Operator[] = ['===', '!=='];

export function operatorsFor(type: ParamType | undefined): Operator[] {
  return type === 'number' ? allOperators : equalityOperators;
}

export function isComplete(
  clause: Clause,
): clause is Clause & { param: string; value: number | string | null } {
  return !!clause.param && clause.value !== undefined && clause.value !== '';
}

function numberError(
  clause: Clause & { value: number },
  info: NumberParamInfo,
): string | undefined {
  const { value } = clause;

  // Membership only constrains =/≠; range operators accept any number.
  if (
    info.availableValues &&
    equalityOperators.includes(clause.operator) &&
    !info.availableValues.includes(value)
  ) {
    const values = info.availableValues
      .map((v) => toDisplayValue(v, { ...info.format, unitAbbreviation: undefined }))
      .join(', ');

    return t('ErrorStrategyConst', { allowedValue: values });
  }

  if (info.format.valueType === 'IntegerNumber' && !Number.isInteger(value)) {
    return t('ErrorStrategyType', { type: t('QuantityValueType.IntegerNumber') });
  }

  if (info.min != null && value < info.min) {
    return t('Validation.Minimum', { limit: info.min });
  }

  if (info.max != null && value > info.max) {
    return t('Validation.Maximum', { limit: info.max });
  }

  return undefined;
}

/** The first reason the clause cannot be saved, or undefined when it is valid. */
export function clauseError(
  clause: Clause,
  info: ParamInfo | undefined,
  applicable: boolean,
): string | undefined {
  if (!applicable) return t('Validation.ConditionParameterNotApplicable');
  if (!isComplete(clause)) return t('Validation.PropertyRequired');

  if (info?.type === 'enum' && !info.options.some((option) => option.value === clause.value)) {
    return t('ErrorStrategyConst', {
      allowedValue: info.options.map((option) => option.title).join(', '),
    });
  }

  if (info?.type === 'number' && isNumber(clause.value)) {
    return numberError({ ...clause, value: clause.value }, info);
  }

  return undefined;
}

function isSingleKeyObject(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value) && Object.keys(value as object).length === 1;
}

function isPrimitiveOrNull(value: unknown): value is number | string | null {
  return value === null || isNumber(value) || isString(value);
}

// An incomplete clause omits the value operand, which tells it apart from a complete clause with value null.
function clauseRule(clause: Clause, complete: boolean) {
  return {
    some: [
      { var: ASSIGNMENTS },
      {
        and: [
          { '===': [{ var: SETUP_PARAMETER_ID }, clause.param ?? ''] },
          { [clause.operator]: complete ? [{ var: VALUE }, clause.value] : [{ var: VALUE }] },
        ],
      },
    ],
  };
}

function parseSome(logic: unknown): Clause | undefined {
  const param = get(logic, ['some', 1, 'and', 0, '===', 1]);
  const operator = Object.keys(get(logic, ['some', 1, 'and', 1]) ?? {})[0] as Operator;
  const operands = get(logic, ['some', 1, 'and', 1, operator]);
  const value = get(operands, 1);
  if (!allOperators.includes(operator) || !isString(param) || !isPrimitiveOrNull(value ?? null)) {
    return undefined;
  }

  const clause: Clause = { param: param || undefined, operator, value };
  return isEqual(clauseRule(clause, get(operands, 'length') === 2), logic) ? clause : undefined;
}

export function parseCondition(json: string | undefined): Clause[] | 'unsupported' {
  if (!json) return [];

  let logic: unknown;
  try {
    logic = JSON.parse(json);
  } catch {
    return 'unsupported';
  }

  if (logic === true) return [];

  const single = parseSome(logic);
  if (single) return [single];

  if (isSingleKeyObject(logic) && Array.isArray(logic.and)) {
    const clauses = logic.and.map(parseSome);
    return clauses.length >= 2 && clauses.every((clause) => clause !== undefined)
      ? clauses
      : 'unsupported';
  }

  return 'unsupported';
}

/**
 * False only for the incomplete-clause shape `serializeCondition` writes (a value comparison with
 * just its `var` operand). Conditions it does not produce (e.g. `true`) count as complete.
 */
export function isConditionComplete(condition: string | null | undefined): boolean {
  const clauses = parseCondition(condition ?? undefined);
  return clauses === 'unsupported' || clauses.every((clause) => clause.value !== undefined);
}

/**
 * Mirrors the backend's rule shape (EvaluationScopeConditionManager): one `some` over the
 * combination's assignments per clause, no wrapper for one clause, `and` for several.
 */
export function serializeCondition(
  clauses: Clause[],
  isValid: (clause: Clause) => boolean = isComplete,
): string {
  if (clauses.length === 0) return 'true';

  const rules = clauses.map((clause) => clauseRule(clause, isValid(clause)));

  return JSON.stringify(rules.length === 1 ? rules[0] : { and: rules });
}
